"use client";

import { useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/button";
import { GoogleSignInButton } from "@/components/google-sign-in-button";
import { InfoCard } from "@/components/info-card";
import { Input } from "@/components/input";
import { CmwLogo } from "@/components/logo";
import { TurnstileWidget, useTurnstile } from "@/components/turnstile";
import { useSignUp } from "@/features/auth/hooks/use-sign-up";
import { googleErrorMessage } from "@/lib/google-error";
import { getApiErrorMessage } from "@/lib/ky/error-message";
import { routes } from "@/lib/routes";

export function SignUpForm() {
  const searchParams = useSearchParams();
  const { turnstileRef, takeTurnstileToken } = useTurnstile();
  const signUp = useSignUp(takeTurnstileToken);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const message = error ?? googleErrorMessage(searchParams.get("error"), searchParams.get("message"));

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const form = new FormData(event.currentTarget);

    signUp.mutate(
      {
        email: String(form.get("email")),
        password: String(form.get("password")),
      },
      {
        onError: async (cause) => setError(await getApiErrorMessage(cause, "Unknown error")),
      },
    );
  }

  return (
    <div className="flex w-full max-w-438 flex-col items-center gap-24 py-64 lg:py-12">
      <CmwLogo type="vertical" className="hidden lg:block" />

      <div className="flex flex-col items-center gap-8 text-center">
        <h1 className="font-sans text-32 font-semibold leading-[1.3] text-brand-dark-blue">Sign up</h1>
        <p className="text-16 text-neutral-black/70">
          {"Already have an account? "}
          <Link href={routes.signIn.url} className="font-semibold text-brand-dark-blue">
            Sign in
          </Link>
        </p>
      </div>

      <GoogleSignInButton label="Sign Up With Google" accountType="practioner" intent="sign-up" />

      <div className="flex w-full items-center gap-8">
        <span className="h-px flex-1 bg-neutral-light-grey" />
        <span className="text-14 text-neutral-black/70">or sign up with email</span>
        <span className="h-px flex-1 bg-neutral-light-grey" />
      </div>

      <form onSubmit={onSubmit} className="flex w-full flex-col gap-24">
        <div className="flex flex-col gap-16">
          <Input
            id="email"
            name="email"
            type="email"
            required
            label="Email Address"
            placeholder="you@example.com"
          />
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            required
            minLength={8}
            label="Password"
            placeholder="Min. 8 Characters"
            message="Use at least 8 characters with a mix of letters and numbers"
            iconRight={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="flex size-24 cursor-pointer items-center justify-center text-neutral-black/70"
              >
                {showPassword ? <EyeSlashIcon className="size-24" /> : <EyeIcon className="size-24" />}
              </button>
            }
          />
          {message && (
            <div role="alert">
              <InfoCard title="Sign up failed" description={message} />
            </div>
          )}
        </div>

        <TurnstileWidget ref={turnstileRef} action="sign-up" />

        <Button type="submit" variant="primary-blue" size="l" disabled={signUp.isPending} className="w-full">
          {signUp.isPending ? <ArrowPathIcon className="size-20 animate-spin" /> : "Sign Up"}
        </Button>
      </form>

      <p className="text-center text-12 leading-[1.4] text-neutral-black/50">
        I agree to the CMW{" "}
        <Link href={routes.privacyPolicy.url} className="text-brand-dark-blue">
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link href={routes.supportAgreement.url} className="text-brand-dark-blue">
          Support Services Agreement
        </Link>
      </p>

      <p className="max-w-344 text-center text-12 leading-[1.4] text-neutral-black/50">
        © 2020-2026 Centre for Muslim Wellbeing. All rights reserved.
      </p>
    </div>
  );
}
